import { apiRequest } from './core/client';
import type { Rule, RuleConditions, RuleAction } from '@/types/email';

export type { Rule, RuleConditions, RuleAction };

// ============================================================================
// Rule Mapping
// ============================================================================

function mapRule(raw: unknown): Rule {
  const data = raw as Record<string, unknown>;
  return {
    id: typeof data.id === 'string' ? data.id : crypto.randomUUID(),
    accountId: typeof data.accountId === 'string' ? data.accountId : '',
    name: typeof data.name === 'string' ? data.name : 'Untitled rule',
    enabled: Boolean(data.enabled),
    priority: typeof data.priority === 'number' ? data.priority : 100,
    conditions: (data.conditions as RuleConditions) ?? { type: 'all', conditions: [] },
    actions: (Array.isArray(data.actions) ? data.actions : []) as RuleAction[],
    runOn: (typeof data.runOn === 'string' ? data.runOn : 'inbound') as Rule['runOn'],
    createdAt:
      typeof data.createdAt === 'string' ? data.createdAt : new Date().toISOString(),
    updatedAt:
      typeof data.updatedAt === 'string' ? data.updatedAt : new Date().toISOString(),
  };
}

// ============================================================================
// Rule Operations
// ============================================================================

export async function fetchRules(accountId: string): Promise<Rule[]> {
  const data = await apiRequest<unknown[]>({
    path: '/rules',
    accountId,
  });
  return data.map(mapRule);
}

export async function fetchRule(accountId: string, ruleId: string): Promise<Rule> {
  const data = await apiRequest<unknown>({
    path: `/rules/${encodeURIComponent(ruleId)}`,
    accountId,
  });
  return mapRule(data);
}

export interface CreateRulePayload {
  name: string;
  enabled?: boolean;
  priority?: number;
  conditions: RuleConditions;
  actions: RuleAction[];
  runOn?: 'inbound' | 'outbound' | 'both';
}

export async function createRule(accountId: string, rule: CreateRulePayload): Promise<Rule> {
  const data = await apiRequest<unknown>({
    path: '/rules',
    method: 'POST',
    accountId,
    body: rule,
  });
  return mapRule(data);
}

export interface UpdateRulePayload {
  name?: string;
  enabled?: boolean;
  priority?: number;
  conditions?: RuleConditions;
  actions?: RuleAction[];
  runOn?: 'inbound' | 'outbound' | 'both';
}

export async function updateRule(
  accountId: string,
  ruleId: string,
  updates: UpdateRulePayload
): Promise<Rule> {
  const data = await apiRequest<unknown>({
    path: `/rules/${encodeURIComponent(ruleId)}`,
    method: 'PUT',
    accountId,
    body: updates,
  });
  return mapRule(data);
}

export async function deleteRule(accountId: string, ruleId: string): Promise<void> {
  await apiRequest({
    path: `/rules/${encodeURIComponent(ruleId)}`,
    method: 'DELETE',
    accountId,
  });
}
